import { createApi } from "@reduxjs/toolkit/query/react";
import { createServiceBaseQuery } from "../../../../../store/client/serviceBaseQuery";
import type { AccessResponse, BulkAccessRequest } from "../../../../../utils/access";
import type { TextRange } from "../../../../../components/HighlightedText";
import tokenManager from "../../../utils/tokenManager";
import { DexibitUIMessage } from "./types";

export interface Conversation {
  id: string;
  accountId: number;
  parentId?: string;
  forkedFromMessageId?: string;
  compactionSummary?: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  messages?: DexibitUIMessage[];
  isUnsaved?: boolean;
  shared?: boolean;
  ownerId?: number | null;
}

/** Who wrote the matching message. The two event types zeus indexes. */
export type ConversationMatchRole = "USER" | "ASSISTANT";

/** The turn a search hit landed in, and whose side of it: together they name one message. */
export interface MatchedTurn {
  turnId: string;
  role: ConversationMatchRole;
}

export interface ConversationExcerpt {
  text: string;
  /** UTF-16 code unit offsets, which index JavaScript strings directly. */
  highlights: TextRange[];
}

// A title hit has no message behind it, so it carries no turn and no role, and its
// excerpt is the title itself, marked by zeus: a stemmed hit is not always a prefix of
// the word it came from, so the client cannot mark it.
export type ConversationMatch =
  | { turnId: null; role: null; excerpt: ConversationExcerpt }
  | (MatchedTurn & { excerpt: ConversationExcerpt });

/**
 * Why a searched conversation is listed. `shown` is the best few, best first, never
 * empty; `total` counts every matching message, so it can exceed `shown.length`.
 */
export interface ConversationMatches {
  total: number;
  shown: ConversationMatch[];
}

/** A row of a searched list. An unsearched row carries no `matches`. */
export interface ConversationSearchResult extends Conversation {
  matches: ConversationMatches;
}

export interface CreateConversationDTO {
  title?: string;
  parentId?: string;
  forkedFromMessageId?: string;
}

export interface UpdateConversationDTO {
  title?: string;
}

export interface GenerateTitleDTO {
  initialMessage: string;
}

export interface MessageFeedbackRequest {
  messageId: string;
  value: 0 | 1; // 0 = dislike, 1 = like
  comment?: string; // Optional user comment
}

export interface MessageFeedbackResponse {
  success: boolean;
  message: string;
}

export interface ForkConversationDTO {
  messageId: string;
  title?: string;
}

export interface CompactConversationDTO {
  messageId?: string;
}

export const conversationService = createApi({
  reducerPath: "conversationService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["Conversations", "Messages", "ConversationAccess", "ConversationSummary"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getConversations: builder.query<Conversation[], void>({
      query: () => "/api/conversations",
      providesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    // Deliberately separate from getConversations rather than a `q` argument on it:
    // that list is navigation infrastructure — ConversationGuard decides 404-vs-render
    // from it and ChatHeader resolves titles from it — so a narrowed list must never
    // occupy its cache entry. Sharing the LIST tag still re-ranks a live search when a
    // conversation is deleted or renamed. zeus answers 400 below three letters or
    // digits, so callers gate with toSearchQuery first.
    searchConversations: builder.query<ConversationSearchResult[], string>({
      query: (q) => ({ url: "/api/conversations", params: { q } }),
      providesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    getConversationById: builder.query<Conversation, { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
      ],
    }),

    createConversation: builder.mutation<Conversation, CreateConversationDTO>({
      query: (conversationData) => ({
        url: "/api/conversations",
        method: "POST",
        body: conversationData,
      }),
      // Callers navigate straight to the new conversation, and ConversationGuard 404s on
      // an id missing from the cached list. The LIST refetch is held back while any other
      // request of this api is in flight (RTK's "delayed" invalidationBehavior), so the
      // new conversation is seeded into the list before the caller's unwrap() resolves.
      async onQueryStarted(_conversationData, api) {
        let conversation: Conversation;
        try {
          ({ data: conversation } = await api.queryFulfilled);
        } catch {
          return;
        }
        // Reads the list rather than mutating a draft: immer's Draft of Conversation (via
        // DexibitUIMessage) is too deep for tsc (TS2589).
        const { data: conversations } =
          conversationService.endpoints.getConversations.select()(api.getState());
        if (!conversations || conversations.some((c) => c.id === conversation.id)) return;
        // Write responses carry a null ownerId, and the sidebar groups conversations by it.
        const ownerId = tokenManager.getUserData()?.id ?? null;
        api.dispatch(
          conversationService.util.updateQueryData("getConversations", undefined, () => [
            { ...conversation, ownerId },
            ...conversations,
          ]),
        );
      },
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    updateConversation: builder.mutation<
      Conversation,
      { conversationId: string; updates: UpdateConversationDTO }
    >({
      query: ({ conversationId, updates }) => ({
        url: `/api/conversations/${conversationId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    updateConversationTitle: builder.mutation<
      Conversation,
      { conversationId: string; updates: UpdateConversationDTO }
    >({
      query: ({ conversationId, updates }) => ({
        url: `/api/conversations/${conversationId}/title`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    deleteConversation: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/api/conversations/${conversationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    getMessages: builder.query<DexibitUIMessage[], { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/messages`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "Messages", id: conversationId },
      ],
    }),

    createMessage: builder.mutation<
      DexibitUIMessage,
      { conversationId: string; message: Partial<DexibitUIMessage> }
    >({
      query: ({ conversationId, message }) => ({
        url: `/api/conversations/${conversationId}/messages`,
        method: "POST",
        body: message,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Messages", id: conversationId },
        { type: "Conversations", id: conversationId },
        { type: "ConversationSummary", id: conversationId },
      ],
    }),

    generateConversationTitle: builder.mutation<
      Conversation,
      { conversationId: string; initialMessage: string }
    >({
      query: ({ conversationId, initialMessage }) => ({
        url: `/api/conversations/${conversationId}/title/generate`,
        method: "POST",
        body: { initialMessage },
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    getConversationAccess: builder.query<AccessResponse, { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/access`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationAccess", id: conversationId },
      ],
    }),

    updateConversationAccess: builder.mutation<
      void,
      { conversationId: string; request: BulkAccessRequest }
    >({
      query: ({ conversationId, request }) => ({
        url: `/api/conversations/${conversationId}/access`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationAccess", id: conversationId },
      ],
    }),

    submitMessageFeedback: builder.mutation<
      MessageFeedbackResponse,
      MessageFeedbackRequest
    >({
      query: ({ messageId, value, comment }) => ({
        url: `/api/messages/${messageId}/feedback`,
        method: "POST",
        body: { value, ...(comment && { comment }) },
      }),
    }),

    forkConversation: builder.mutation<
      Conversation,
      { conversationId: string; data: ForkConversationDTO }
    >({
      query: ({ conversationId, data }) => ({
        url: `/api/conversations/${conversationId}/fork`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    compactConversation: builder.mutation<
      Conversation,
      { conversationId: string; data: CompactConversationDTO }
    >({
      query: ({ conversationId, data }) => ({
        url: `/api/conversations/${conversationId}/compact`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    cancelChatStream: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/api/chat/${conversationId}/stream`,
        method: "DELETE",
      }),
    }),

    getConversationSummary: builder.query<
      { summary: string },
      { conversationId: string }
    >({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/summary`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationSummary", id: conversationId },
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useSearchConversationsQuery,
  useGetConversationByIdQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useUpdateConversationTitleMutation,
  useDeleteConversationMutation,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useCreateMessageMutation,
  useGenerateConversationTitleMutation,
  useGetConversationAccessQuery,
  useUpdateConversationAccessMutation,
  useSubmitMessageFeedbackMutation,
  useForkConversationMutation,
  useCompactConversationMutation,
  useCancelChatStreamMutation,
  useGetConversationSummaryQuery,
  useLazyGetConversationSummaryQuery,
} = conversationService;
